// ─────────────────────────────────────────────────────────────────────────────
// Crew — Investor / internal metrics dashboard (Agent D).
//
// The funnel IS the product's strategic core and fundraise artefact. This surface
// renders the REAL numbers the Worker computes from D1 — never mocked. It lives at
// the #/metrics hash route (wired in main.tsx) so it survives Agent B's App.tsx
// rewrite and is reachable without touching the consumer UI.
//
// Auth: GET /api/metrics is gated by a session bearer token AND the METRICS_TOKEN
// secret (sent as x-metrics-token). The operator pastes the token below; it is held
// only in component state (never persisted) so a shared screen can't leak it.
// ─────────────────────────────────────────────────────────────────────────────

import { useState } from 'react';
import { api, ApiError, type MetricsResponse, type DerivedMetricValue } from './lib/api';
import { FUNNEL_EVENTS, type FunnelEvent } from './lib/analytics';

// Human labels for the frozen taxonomy — display only; never reorder the source.
const EVENT_LABELS: Record<FunnelEvent, string> = {
  group_created: 'Group created',
  member_joined: 'Member joined',
  goal_created: 'Goal created',
  goal_committed: 'Goal committed',
  time_locked: 'Time locked',
  activity_viewed: 'Activity viewed',
  intent_shortlisted: 'Intent shortlisted',
  intent_committed: 'Intent committed',
  click_out: 'Click-out',
  booking_reported: 'Booking reported',
  clo_verified_spend: 'CLO verified spend',
  group_retained: 'Group retained',
};

const DERIVED_LABELS: Record<string, string> = {
  activation_rate: 'Activation rate',
  retention_wn: 'Retention (Wn ≈ day 7)',
  retention_mn: 'Retention (Mn ≈ day 30)',
  intent_to_conversion: 'Intent → conversion',
  gmv_influenced: 'GMV influenced',
  take_rate: 'Take rate',
  cac_vs_early_ltv: 'CAC vs early LTV',
};

function pct(n: number): string {
  return `${(n * 100).toFixed(1)}%`;
}

// Render a derived cell honestly: null → the note (no fabricated number).
function formatDerived(d: DerivedMetricValue): string {
  if (d.value === null) return '—';
  if (d.unit === 'ratio') return pct(d.value);
  if (d.unit === 'pence') return `£${(d.value / 100).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  return String(d.value);
}

export default function MetricsDashboard() {
  const [token, setToken] = useState('');
  const [data, setData] = useState<MetricsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  async function load(e?: React.FormEvent) {
    e?.preventDefault();
    setLoading(true);
    setError(null);
    try {
      setData(await api.metrics(token || undefined));
    } catch (err) {
      if (err instanceof ApiError) {
        setError(
          err.status === 401
            ? 'Unauthorised — paste a valid metrics token (and ensure you are signed in).'
            : err.message,
        );
      } else {
        setError('Could not reach the metrics endpoint.');
      }
      setData(null);
    } finally {
      setLoading(false);
    }
  }

  // Funnel in canonical order, top count for proportional bars.
  const funnelRows = data
    ? FUNNEL_EVENTS.map((ev) => ({ event: ev, count: data.funnel[ev] ?? 0 }))
    : [];
  const topCount = funnelRows.reduce((max, r) => Math.max(max, r.count), 0);

  return (
    <main className="mx-auto max-w-4xl p-6">
      <header className="mb-6">
        <h1 className="text-3xl font-semibold tracking-tight text-ink-900">Crew — Metrics</h1>
        <p className="mt-1 text-sm text-ink-700">
          Real funnel, conversion and derived metrics from D1. The high-intent demand
          layer for group experiences — measured, never mocked.
        </p>
      </header>

      <form onSubmit={load} className="mb-8 flex flex-wrap items-center gap-3">
        <input
          type="password"
          value={token}
          onChange={(e) => setToken(e.target.value)}
          placeholder="Metrics token"
          className="flex-1 min-w-[12rem] rounded-xl2 border border-sand-200 bg-white/70 px-4 py-2 text-sm text-ink-900 outline-none ring-coral-500/0 focus:ring-2 focus:ring-coral-500/40"
        />
        <button
          type="submit"
          disabled={loading}
          className="rounded-full bg-coral-500 px-5 py-2 text-sm font-medium text-white disabled:opacity-50"
        >
          {loading ? 'Loading…' : data ? 'Refresh' : 'Load metrics'}
        </button>
      </form>

      {error && (
        <p className="mb-6 rounded-xl2 bg-coral-500/10 px-4 py-3 text-sm text-coral-600 ring-1 ring-coral-500/20">
          {error}
        </p>
      )}

      {data && (
        <div className="space-y-8">
          {/* Totals */}
          <section className="grid grid-cols-2 gap-4 sm:grid-cols-3">
            <Stat label="Groups" value={String(data.totals.groups)} />
            <Stat label="Unlocked groups" value={String(data.totals.unlockedGroups)} />
            <Stat
              label="Activation"
              value={formatDerived(data.derived.activation_rate ?? { value: null, unit: 'ratio' })}
            />
          </section>

          {/* Funnel */}
          <section>
            <h2 className="mb-3 text-lg font-semibold text-ink-900">Funnel</h2>
            <div className="space-y-2">
              {funnelRows.map((row, i) => {
                const prev = i > 0 ? funnelRows[i - 1].count : null;
                const stepRate = prev && prev > 0 ? row.count / prev : null;
                const width = topCount > 0 ? Math.max(2, (row.count / topCount) * 100) : 0;
                return (
                  <div key={row.event} className="rounded-xl2 bg-white/70 p-3 ring-1 ring-sand-200">
                    <div className="flex items-baseline justify-between">
                      <span className="text-sm font-medium text-ink-900">{EVENT_LABELS[row.event]}</span>
                      <span className="text-sm tabular-nums text-ink-700">
                        {row.count.toLocaleString('en-GB')}
                        {stepRate !== null && (
                          <span className="ml-2 text-xs text-ink-700/60">{pct(stepRate)} of prev</span>
                        )}
                      </span>
                    </div>
                    <div className="mt-2 h-2 w-full rounded-full bg-sand-100">
                      <div
                        className="h-2 rounded-full bg-gradient-to-r from-coral-500 to-amber-400"
                        style={{ width: `${width}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </section>

          {/* Conversions */}
          <section>
            <h2 className="mb-3 text-lg font-semibold text-ink-900">Conversions</h2>
            {Object.keys(data.conversions).length === 0 ? (
              <p className="text-sm text-ink-700/60">No conversion events recorded yet.</p>
            ) : (
              <div className="overflow-hidden rounded-xl2 ring-1 ring-sand-200">
                <table className="w-full text-left text-sm">
                  <thead className="bg-sand-100 text-ink-700">
                    <tr>
                      <th className="px-4 py-2 font-medium">Type</th>
                      <th className="px-4 py-2 text-right font-medium">Count</th>
                      <th className="px-4 py-2 text-right font-medium">Value</th>
                      <th className="px-4 py-2 text-right font-medium">Commission</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(data.conversions).map(([type, c]) => (
                      <tr key={type} className="border-t border-sand-200 bg-white/70">
                        <td className="px-4 py-2 text-ink-900">{type}</td>
                        <td className="px-4 py-2 text-right tabular-nums text-ink-700">{c.count}</td>
                        <td className="px-4 py-2 text-right tabular-nums text-ink-700">
                          £{(c.valuePence / 100).toLocaleString('en-GB', { minimumFractionDigits: 2 })}
                        </td>
                        <td className="px-4 py-2 text-right tabular-nums text-ink-700">
                          £{(c.commissionPence / 100).toLocaleString('en-GB', { minimumFractionDigits: 2 })}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          {/* Derived */}
          <section>
            <h2 className="mb-3 text-lg font-semibold text-ink-900">Derived metrics</h2>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {Object.entries(data.derived).map(([key, d]) => (
                <div key={key} className="rounded-xl2 bg-white/70 p-4 ring-1 ring-sand-200">
                  <div className="flex items-baseline justify-between">
                    <span className="text-sm font-medium text-ink-900">{DERIVED_LABELS[key] ?? key}</span>
                    <span className="text-lg font-semibold tabular-nums text-ink-900">{formatDerived(d)}</span>
                  </div>
                  {d.note && <p className="mt-1 text-xs text-ink-700/60">{d.note}</p>}
                </div>
              ))}
            </div>
          </section>

          <p className="pt-2 text-xs text-ink-700/50">
            Numbers are computed server-side from D1. Cells showing “—” have no input
            feed yet and are flagged honestly rather than fabricated.
          </p>
        </div>
      )}
    </main>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl2 bg-white/70 p-4 ring-1 ring-sand-200">
      <p className="text-xs uppercase tracking-wide text-ink-700/60">{label}</p>
      <p className="mt-1 text-2xl font-semibold tabular-nums text-ink-900">{value}</p>
    </div>
  );
}
